import { useEffect, useState } from 'react'

import { DateTime } from 'luxon'
import { FiX } from 'react-icons/fi'
import { MdBattery20, MdBattery50, MdBattery80, MdBatteryFull, MdThermostat } from 'react-icons/md'

import { useDashboard } from '@/hooks'

import { useTrackerMapContext } from './TrackerMapContext'
import { rowStatusIcon } from './TrackerTable'
import { RowStatus } from './types'
import {
  SHIPMENT_STATE_BADGE_CLASS,
  SHIPMENT_STATE_LABELS,
  formatTemp,
  getRowStatus,
  statusColor,
  tsToMillis,
} from './utils'

const StatusRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-base-200 last:border-b-0">
    <span className="text-xs text-base-content/70">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
)

const batteryIcon = (percent: number | null) => {
  if (percent == null) return null
  const cls = `w-5 h-5 ${percent <= 20 ? 'text-red-500' : percent <= 50 ? 'text-amber-500' : 'text-emerald-600'}`
  if (percent <= 20) return <MdBattery20 className={cls} />
  if (percent <= 50) return <MdBattery50 className={cls} />
  if (percent <= 80) return <MdBattery80 className={cls} />
  return <MdBatteryFull className={cls} />
}

const statusTitle = (status: RowStatus): string => {
  switch (status) {
    case 'planned':
      return 'No tracker assigned'
    case 'completed':
      return 'Completed shipment'
    case 'stale':
      return 'Offline'
    case 'warning':
      return 'Low battery'
    default:
      return 'Online'
  }
}

/**
 * Floating quick-view card for the selected map row, mirroring AssetQuickView:
 * positioned under the #quick-view-toggle button, gated on mapState.showQuickView.
 */
export const TrackerQuickView = () => {
  const context = useTrackerMapContext()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const row = context.currentRow

  // Position just below the quick-view toggle button, inside the map container.
  useEffect(() => {
    const button = document.getElementById('quick-view-toggle')
    if (button) {
      const rect = button.getBoundingClientRect()
      const mapContainer = button.closest('.flex-1.relative')
      if (mapContainer) {
        const containerRect = mapContainer.getBoundingClientRect()
        setPosition({
          top: rect.bottom - containerRect.top + 4,
          left: rect.left - containerRect.left,
        })
      }
    }
  }, [context.currentRowId, context.mapState.showQuickView])

  // In history mode the trail's per-point popups replace the side quick view.
  if (context.historyMode) return null
  if (!context.mapState.showQuickView) return null
  if (!row) return null

  const status = getRowStatus(row)
  const lastUpdateMs = Math.max(
    0,
    ...[tsToMillis(row.locationTs), tsToMillis(row.temperatureCelsiusTs), tsToMillis(row.batteryPercentTs)].filter(
      (v): v is number => v != null
    )
  )

  const isSplit = context.sidebarState.layoutMode === 'split'
  const wrapperStyle = isSplit ? { bottom: 16, left: position.left } : { top: position.top, left: position.left }

  return (
    <div
      className="absolute z-40 w-72 bg-base-100 rounded-lg shadow-lg border border-base-300 overflow-hidden"
      style={wrapperStyle}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-base-300">
        <h3 className="font-medium text-base truncate flex-1 pr-2">{row.shipmentName ?? row.label ?? 'Tracker'}</h3>
        <button
          onClick={() => context.setMapState((prev) => ({ ...prev, showQuickView: false }))}
          className="btn btn-ghost btn-xs btn-circle"
          title="Close"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Status indicators */}
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-base-200">
          <div className="flex items-center gap-1" title={statusTitle(status)}>
            {rowStatusIcon(status)}
          </div>
          {row.batteryPercent != null && (
            <div className="flex items-center gap-1" title={`Battery ${Math.round(row.batteryPercent)}%`}>
              {batteryIcon(row.batteryPercent)}
              <span className="text-xs">{Math.round(row.batteryPercent)}%</span>
            </div>
          )}
          {row.temperatureCelsius != null && (
            <div className="flex items-center gap-1" title="Temperature">
              <MdThermostat className="w-5 h-5" style={{ color: statusColor(status) }} />
              <span className="text-xs">{formatTemp(row.temperatureCelsius, units)}</span>
            </div>
          )}
          {row.shipmentState && (
            <span className={`badge badge-sm ml-auto ${SHIPMENT_STATE_BADGE_CLASS[row.shipmentState]}`}>
              {SHIPMENT_STATE_LABELS[row.shipmentState]}
            </span>
          )}
        </div>

        {/* Details */}
        <div className="space-y-0">
          <div className="text-xs text-base-content/70">Location</div>
          <div className="pl-2 flex items-center gap-2 text-xs">
            {row.location ? `${row.location.latitude.toFixed(4)}, ${row.location.longitude.toFixed(4)}` : '—'}
          </div>

          {(row.originName || row.destinationName) && (
            <StatusRow label="Route">
              <span className="text-xs truncate">
                {row.originName ?? '—'} → {row.destinationName ?? '—'}
              </span>
            </StatusRow>
          )}

          {row.trackerId != null && row.shipmentName != null && (
            <StatusRow label="Tracker">
              <span className="text-xs">{row.label}</span>
            </StatusRow>
          )}

          <StatusRow label={row.asOf === 'shipmentEnd' ? 'As of Shipment End' : 'Last Update'}>
            <span className="text-xs">{lastUpdateMs ? DateTime.fromMillis(lastUpdateMs).toRelative() : '—'}</span>
          </StatusRow>
        </div>

        {/* Actions */}
        {row.trackerId != null && (
          <div className="mt-3 pt-3 border-t border-base-200">
            <button onClick={context.openDetails} className="btn btn-primary btn-sm w-full">
              View Full Details
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
